import React, { useMemo, useState } from "react";
import { Box, Button, ButtonGroup, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import moment from "moment-timezone";
import { toCurrency } from "components-library";
import { useCurrency } from "@store/actions/customActions";
import { useGetSiteTimezone } from "@store/actions/siteMetaActions";
import { useUserStats } from "@store/actions/userActions";
import { useAuthContext } from "@providers/AuthProvider";

const monthRange = (zone, offset) => {
  const month = moment.tz(zone).subtract(offset, "months");
  return { from: month.clone().startOf("month").format("YYYY-MM-DD"), to: month.clone().endOf("month").format("YYYY-MM-DD") };
};
const count = (value, partial) => value === null ? "Not tracked" : `${value.toLocaleString()}${partial ? " recorded" : ""}`;

export default function UserStatsTable() {
  const { isSuperAdmin, userLoading, userError } = useAuthContext();
  const { data: currencyData, loading: currencyLoading, error: currencyError } = useCurrency();
  const { data: timezoneData, loading: timezoneLoading, error: timezoneError } = useGetSiteTimezone();
  if (userLoading) return <CircularProgress aria-label="Loading your account" />;
  if (userError) return <Typography>Your account could not be loaded. Please refresh this page.</Typography>;
  if (!isSuperAdmin) return <Typography>You do not have access to user statistics.</Typography>;
  if (currencyLoading || timezoneLoading) return <CircularProgress aria-label="Loading report settings" />;
  if (currencyError || timezoneError || !currencyData?.currency || !timezoneData?.timezone || !moment.tz.zone(timezoneData.timezone)) {
    return <Typography>Report settings could not be loaded. Please refresh this page.</Typography>;
  }
  return <UserStatsReport currency={currencyData.currency} zone={timezoneData.timezone} />;
}

function UserStatsReport({ currency, zone }) {
  const [draft, setDraft] = useState(() => monthRange(zone, 0));
  const [range, setRange] = useState(() => monthRange(zone, 0));
  const [sort, setSort] = useState("closedRevenue");
  const valid = draft.from && draft.to && draft.from <= draft.to;
  const chooseMonth = (offset) => { const selected = monthRange(zone, offset); setDraft(selected); setRange(selected); };

  const { data, loading, error, refetch } = useUserStats({ variables: range });
  const report = !loading && !error ? data?.userStats : null;

  const rows = useMemo(() => [...(report?.rows || [])].sort((a, b) => sort === "name"
    ? a.name.localeCompare(b.name) : (b[sort] ?? -1) - (a[sort] ?? -1) || a.name.localeCompare(b.name)), [report, sort]);

  const columns = [["name", "User"], ["notesAdded", "Notes added / updated"], ["quotesSent", "Quotes sent"],
    ["emailsSent", "Emails sent"], ["uniqueLeadsEmailed", "Unique leads emailed"], ["optionsSent", "Options sent"],
    ["paidQuotes", "Quotes with payments"], ["closedRevenue", `Closed revenue (${currency})`]];
  return <Box>
    <Button component={RouterLink} to="/users" color="primary">Back to users</Button>
    <Typography variant="h5">User performance</Typography>
    <Box mt={2} mb={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
      <TextField type="date" label="From" variant="outlined" size="small" value={draft.from} InputLabelProps={{ shrink: true }}
        onChange={(event) => setDraft({ ...draft, from: event.target.value })} />
      <TextField type="date" label="To" variant="outlined" size="small" value={draft.to} InputLabelProps={{ shrink: true }}
        inputProps={{ min: draft.from }} onChange={(event) => setDraft({ ...draft, to: event.target.value })} />
      <Button variant="contained" color="primary" disabled={!valid} onClick={() => setRange({ ...draft })}>Apply</Button>
      <ButtonGroup color="primary" size="small">
        {[0, 1, 2, 3].map((offset) => {
          const selected = monthRange(zone, offset);
          return <Button key={offset} variant={range.from === selected.from && range.to === selected.to ? "contained" : "outlined"}
            onClick={() => chooseMonth(offset)}>{offset === 0 ? "Current month" : moment.tz(zone).subtract(offset, "months").format("MMMM YYYY")}</Button>;
        })}
      </ButtonGroup>
    </Box>
    {!valid && <Typography color="error">Choose an end date on or after the start date.</Typography>}
    <Typography variant="body2" color="textSecondary">{moment(range.from).format("D MMM YYYY")} to {moment(range.to).format("D MMM YYYY")} · {zone.split("/").pop().replace(/_/g, " ")} time. Select a column to sort.</Typography>
    {loading && <Box my={3}><CircularProgress aria-label="Loading user statistics" /></Box>}
    {error && <Box my={3}><Typography color="error">User statistics could not be loaded. Please try again.</Typography><Button onClick={() => refetch()}>Try again</Button></Box>}
    {report && <>
      <Box my={2}>
        <Typography variant="body2" color="textSecondary">
          Notes, emails and options are recorded by user from {moment(report.trackingStartedAt).tz(zone).format("D MMM YYYY, h:mm a")}. Earlier activity is not available by user.
          {report.partialTracking ? " These columns show only activity recorded since that time within your selected dates." : ""}
        </Typography>
        <details style={{ marginTop: 8 }}><summary style={{ cursor: "pointer" }}>About these figures</summary>
          <Typography variant="body2" color="textSecondary">Historical quotes use their creator and latest recorded send date; new quote sends use the sender. Closed revenue is payments received during these dates, excluding tax, attributed to the quote creator. Deposits are included. Emails include quote and option emails. Activity without a recorded user is not assigned to anyone.</Typography>
        </details>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="User performance statistics">
          <TableHead><TableRow>{columns.map(([key, label]) => <TableCell key={key} align={key === "name" ? "left" : "right"} style={key === "name" ? { position: "sticky", left: 0, zIndex: 2, background: "white", minWidth: 170, maxWidth: 190 } : {}}>
            <Button size="small" onClick={() => setSort(key)} style={{ textTransform: "none", fontWeight: 600 }}>{label}{sort === key ? (key === "name" ? " ↑" : " ↓") : ""}</Button>
          </TableCell>)}</TableRow></TableHead>
          <TableBody>{rows.map((row) => <TableRow key={row.id}>
            <TableCell style={{ position: "sticky", left: 0, zIndex: 1, background: "white", minWidth: 170, maxWidth: 190 }}><Typography variant="body2">{row.name}</Typography><Typography variant="caption" color="textSecondary" style={{ display: "block", overflowWrap: "anywhere" }}>{row.email}</Typography></TableCell>
            {columns.slice(1).map(([key]) => <TableCell key={key} align="right" style={{ whiteSpace: "nowrap" }}>{key === "closedRevenue" ? toCurrency(row[key] / 100, currency) : count(row[key], report.partialTracking && ["notesAdded", "emailsSent", "uniqueLeadsEmailed", "optionsSent"].includes(key))}</TableCell>)}
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && <Box my={3}><Typography>No users found.</Typography></Box>}
    </>}
  </Box>;
}
